import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Grid, Card, CardContent, Typography, Box, TextField, Button, Stack, Switch, FormControlLabel, Select, MenuItem, FormControl, InputLabel, Chip, IconButton, Divider, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { getCourseDetail, createCourse, updateCourse, getInstructors } from '../../api/endpoints/courses';

const categories = ['Astrology', 'Palmistry', 'Numerology', 'Vastu', 'Tarot', 'Gemology', 'Yoga', 'Meditation'];
const levels = ['beginner', 'intermediate', 'advanced'];
const languages = ['Hindi', 'English', 'Tamil', 'Telugu', 'Bengali', 'Marathi', 'Gujarati'];

const CourseForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState({
    title: '', subtitle: '', description: '', category: '', instructorId: '',
    price: '', comparePrice: '', level: 'beginner', language: 'hindi', isPublished: false,
  });
  const [curriculum, setCurriculum] = useState([]);
  const [moduleTitle, setModuleTitle] = useState('');
  const [requirements, setRequirements] = useState([]);
  const [reqInput, setReqInput] = useState('');
  const [outcomes, setOutcomes] = useState([]);
  const [outcomeInput, setOutcomeInput] = useState('');
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [instRes] = await Promise.all([getInstructors()]);
      setInstructors(instRes.data.data || []);
      if (isEdit) {
        const courseRes = await getCourseDetail(id);
        const course = courseRes.data.data;
        setForm({
          title: course.title || '', subtitle: course.subtitle || '', description: course.description || '',
          category: course.category || '', instructorId: course.instructorId?._id || course.instructorId || '',
          price: course.price || '', comparePrice: course.comparePrice || '',
          level: course.level || 'beginner', language: course.language || 'hindi', isPublished: course.isPublished || false,
        });
        setCurriculum(course.curriculum || []);
        setRequirements(course.requirements || []);
        setOutcomes(course.learningOutcomes || []);
      }
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const addModule = () => {
    if (!moduleTitle.trim()) return;
    setCurriculum((prev) => [...prev, { moduleTitle: moduleTitle.trim(), lessons: [] }]);
    setModuleTitle('');
  };

  const removeModule = (idx) => setCurriculum((prev) => prev.filter((_, i) => i !== idx));

  const addLesson = (modIdx) => {
    setCurriculum((prev) => prev.map((m, i) => i === modIdx ? { ...m, lessons: [...(m.lessons || []), { title: '', videoUrl: '', duration: 0, isFree: false }] } : m));
  };

  const updateLesson = (modIdx, lesIdx, field, value) => {
    setCurriculum((prev) => prev.map((m, i) => i === modIdx ? {
      ...m, lessons: m.lessons.map((l, j) => j === lesIdx ? { ...l, [field]: value } : l),
    } : m));
  };

  const removeLesson = (modIdx, lesIdx) => {
    setCurriculum((prev) => prev.map((m, i) => i === modIdx ? { ...m, lessons: m.lessons.filter((_, j) => j !== lesIdx) } : m));
  };

  const handleSave = async () => {
    if (!form.title || !form.category || !form.price) {
      toast.error('Title, category, and price are required');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        ...form, price: Number(form.price), comparePrice: Number(form.comparePrice) || undefined,
        totalLessons: curriculum.reduce((s, m) => s + (m.lessons?.length || 0), 0),
        curriculum, requirements, learningOutcomes: outcomes,
      };
      if (isEdit) {
        await updateCourse(id, payload);
        toast.success('Course updated');
      } else {
        await createCourse(payload);
        toast.success('Course created');
      }
      navigate('/courses');
    } catch {
      toast.error('Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/courses')} sx={{ mb: 2 }}>Back</Button>
      <Typography variant="h5" fontWeight={700} gutterBottom>{isEdit ? 'Edit Course' : 'Add Course'}</Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>Course Details</Typography>
              <Stack spacing={2}>
                <TextField label="Title" value={form.title} onChange={handleChange('title')} fullWidth required />
                <TextField label="Subtitle" value={form.subtitle} onChange={handleChange('subtitle')} fullWidth />
                <TextField label="Description" value={form.description} onChange={handleChange('description')} fullWidth multiline rows={4} />
                <Grid container spacing={2}>
                  <Grid item xs={6}><FormControl fullWidth size="small"><InputLabel>Category</InputLabel><Select value={form.category} label="Category" onChange={handleChange('category')}>{categories.map((c) => <MenuItem key={c} value={c.toLowerCase()}>{c}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item xs={6}><FormControl fullWidth size="small"><InputLabel>Instructor</InputLabel><Select value={form.instructorId} label="Instructor" onChange={handleChange('instructorId')}>{instructors.map((i) => <MenuItem key={i._id} value={i._id}>{i.name}</MenuItem>)}</Select></FormControl></Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={4}><TextField label="Price (₹)" type="number" value={form.price} onChange={handleChange('price')} fullWidth required /></Grid>
                  <Grid item xs={4}><TextField label="Compare Price" type="number" value={form.comparePrice} onChange={handleChange('comparePrice')} fullWidth /></Grid>
                  <Grid item xs={4}><FormControl fullWidth size="small"><InputLabel>Level</InputLabel><Select value={form.level} label="Level" onChange={handleChange('level')}>{levels.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}</Select></FormControl></Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={6}><FormControl fullWidth size="small"><InputLabel>Language</InputLabel><Select value={form.language} label="Language" onChange={handleChange('language')}>{languages.map((l) => <MenuItem key={l} value={l.toLowerCase()}>{l}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item xs={6}><FormControlLabel control={<Switch checked={form.isPublished} onChange={(e) => setForm((p) => ({ ...p, isPublished: e.target.checked }))} />} label="Published" sx={{ mt: 1 }} /></Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>Curriculum</Typography>
              {curriculum.map((mod, mi) => (
                <Box key={mi} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2" fontWeight={600}>{mod.moduleTitle}</Typography>
                    <IconButton size="small" onClick={() => removeModule(mi)}><DeleteIcon fontSize="small" /></IconButton>
                  </Stack>
                  {mod.lessons?.map((les, li) => (
                    <Stack key={li} direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                      <TextField size="small" placeholder="Title" value={les.title} onChange={(e) => updateLesson(mi, li, 'title', e.target.value)} sx={{ flex: 2 }} />
                      <TextField size="small" placeholder="Duration (min)" type="number" value={les.duration} onChange={(e) => updateLesson(mi, li, 'duration', +e.target.value)} sx={{ flex: 1 }} />
                      <FormControlLabel control={<Switch size="small" checked={les.isFree} onChange={(e) => updateLesson(mi, li, 'isFree', e.target.checked)} />} label="Free" />
                      <IconButton size="small" onClick={() => removeLesson(mi, li)}><DeleteIcon fontSize="small" /></IconButton>
                    </Stack>
                  ))}
                  <Button size="small" onClick={() => addLesson(mi)} sx={{ mt: 1 }}>+ Add Lesson</Button>
                </Box>
              ))}
              <Stack direction="row" spacing={1}>
                <TextField size="small" placeholder="Module title" value={moduleTitle} onChange={(e) => setModuleTitle(e.target.value)} />
                <Button variant="outlined" onClick={addModule}>Add Module</Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Requirements</Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <TextField size="small" placeholder="Add requirement" value={reqInput} onChange={(e) => setReqInput(e.target.value)} fullWidth />
                <Button size="small" onClick={() => { if (reqInput.trim()) { setRequirements((p) => [...p, reqInput.trim()]); setReqInput(''); } }}>Add</Button>
              </Stack>
              {requirements.map((r, i) => (
                <Chip key={i} label={r} onDelete={() => setRequirements((p) => p.filter((_, j) => j !== i))} size="small" sx={{ m: 0.3 }} />
              ))}
            </CardContent>
          </Card>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Learning Outcomes</Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <TextField size="small" placeholder="Add outcome" value={outcomeInput} onChange={(e) => setOutcomeInput(e.target.value)} fullWidth />
                <Button size="small" onClick={() => { if (outcomeInput.trim()) { setOutcomes((p) => [...p, outcomeInput.trim()]); setOutcomeInput(''); } }}>Add</Button>
              </Stack>
              {outcomes.map((o, i) => (
                <Chip key={i} label={o} onDelete={() => setOutcomes((p) => p.filter((_, j) => j !== i))} size="small" sx={{ m: 0.3 }} />
              ))}
            </CardContent>
          </Card>
          <Button variant="contained" size="large" fullWidth onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Update Course' : 'Create Course')}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CourseForm;
